import * as THREE from 'three';
import { LANE_WIDTH, TRACK } from '../game/tuning';
import { PALETTE } from '../assets/palette';

interface Cell {
  x: number;
  z: number;
  active: boolean;
  super: boolean;
  /** >0 while playing the collect pop (seconds remaining). */
  popTime: number;
}

const MAX_CELLS = 96;
const POP_DURATION = 0.24;
const dummy = new THREE.Object3D();

/** Energy cells as two InstancedMeshes (normal + supercell). */
export class CellSystem {
  readonly group = new THREE.Group();

  private readonly cells: Cell[] = [];
  private readonly normalMesh: THREE.InstancedMesh;
  private readonly superMesh: THREE.InstancedMesh;
  private readonly ringMesh: THREE.InstancedMesh;
  private readonly superRingMesh: THREE.InstancedMesh;
  private readonly disposables: Array<{ dispose(): void }> = [];

  constructor() {
    const geometry = new THREE.OctahedronGeometry(0.34, 0);
    // Supercell reads as a distinct form: a spinning data-core cube inside a
    // gyroscope ring, not a bigger tint of the normal diamond.
    const superGeometry = new THREE.BoxGeometry(0.52, 0.52, 0.52);
    const ringGeometry = new THREE.TorusGeometry(0.52, 0.028, 8, 24);
    const superRingGeometry = new THREE.TorusGeometry(0.72, 0.04, 8, 28);
    const material = new THREE.MeshStandardMaterial({
      color: '#0e1406',
      emissive: PALETTE.cell,
      emissiveIntensity: 1.6,
      roughness: 0.3,
      metalness: 0.2,
    });
    const superMaterial = new THREE.MeshStandardMaterial({
      color: '#161204',
      emissive: PALETTE.supercell,
      emissiveIntensity: 2.0,
      roughness: 0.3,
      metalness: 0.2,
    });
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: '#0a0f04',
      emissive: PALETTE.cell,
      emissiveIntensity: 0.85,
      roughness: 0.4,
      metalness: 0.3,
    });
    const superRingMaterial = new THREE.MeshStandardMaterial({
      color: '#12100a',
      emissive: PALETTE.supercell,
      emissiveIntensity: 1.1,
      roughness: 0.4,
      metalness: 0.3,
    });
    this.disposables.push(
      geometry, superGeometry, ringGeometry, superRingGeometry,
      material, superMaterial, ringMaterial, superRingMaterial,
    );

    this.normalMesh = new THREE.InstancedMesh(geometry, material, MAX_CELLS);
    this.superMesh = new THREE.InstancedMesh(superGeometry, superMaterial, 16);
    this.ringMesh = new THREE.InstancedMesh(ringGeometry, ringMaterial, MAX_CELLS);
    this.superRingMesh = new THREE.InstancedMesh(superRingGeometry, superRingMaterial, 16);
    for (const mesh of [this.normalMesh, this.superMesh, this.ringMesh, this.superRingMesh]) {
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.count = 0;
      mesh.frustumCulled = false;
      this.group.add(mesh);
    }

    for (let i = 0; i < MAX_CELLS; i += 1) {
      this.cells.push({ x: 0, z: 0, active: false, super: false, popTime: 0 });
    }
  }

  spawn(lane: -1 | 0 | 1, z: number, isSuper = false): boolean {
    const slot = this.cells.find((c) => !c.active && c.popTime <= 0);
    if (!slot) return false;
    slot.active = true;
    slot.super = isSuper;
    slot.x = lane * LANE_WIDTH;
    slot.z = z;
    slot.popTime = 0;
    return true;
  }

  /**
   * Advances cells, animates spin/pop, and returns collected counts.
   * Collection radius grows slightly with speed (soft magnetism).
   */
  update(
    distanceDelta: number,
    delta: number,
    elapsed: number,
    playerX: number,
    magnetRadius: number,
    collecting: boolean,
  ): { collected: number; superCollected: number } {
    let collected = 0;
    let superCollected = 0;
    let normalCount = 0;
    let superCount = 0;

    for (const cell of this.cells) {
      if (!cell.active && cell.popTime <= 0) continue;
      cell.z += distanceDelta;

      if (cell.active && cell.z > TRACK.despawnZ) {
        cell.active = false;
        continue;
      }

      if (cell.active && collecting && Math.abs(cell.z) < 1.2) {
        const distance = Math.hypot(cell.x - playerX, cell.z);
        if (distance < magnetRadius) {
          cell.active = false;
          cell.popTime = POP_DURATION;
          if (cell.super) superCollected += 1;
          else collected += 1;
        }
      }

      let scale = 1;
      let y = 0.85 + Math.sin(elapsed * 4 + cell.x * 2 + cell.z * 0.5) * 0.1;
      if (cell.popTime > 0) {
        cell.popTime -= delta;
        const t = 1 - Math.max(cell.popTime, 0) / POP_DURATION;
        scale = 1 + t * 1.4;
        y += t * 1.1;
        if (cell.popTime <= 0) continue;
      }

      const finalScale = scale * (cell.popTime > 0 ? 1 - (1 - cell.popTime / POP_DURATION) * 0.6 : 1);
      dummy.position.set(cell.x, y, cell.z);
      dummy.rotation.set(0, elapsed * 2.4 + cell.x, cell.super ? elapsed * 1.7 : 0);
      dummy.scale.setScalar(finalScale);
      dummy.updateMatrix();
      if (cell.super) {
        if (superCount < this.superMesh.instanceMatrix.count) {
          this.superMesh.setMatrixAt(superCount, dummy.matrix);
          // Orbit ring tilts on its own axis for a gyroscope read.
          dummy.rotation.set(elapsed * 1.9 + cell.z, elapsed * 1.1, Math.PI / 3);
          dummy.updateMatrix();
          this.superRingMesh.setMatrixAt(superCount, dummy.matrix);
          superCount += 1;
        }
      } else if (normalCount < this.normalMesh.instanceMatrix.count) {
        this.normalMesh.setMatrixAt(normalCount, dummy.matrix);
        dummy.rotation.set(Math.PI / 2.6, elapsed * 2.4 + cell.x, 0);
        dummy.updateMatrix();
        this.ringMesh.setMatrixAt(normalCount, dummy.matrix);
        normalCount += 1;
      }
    }

    this.normalMesh.count = normalCount;
    this.superMesh.count = superCount;
    this.ringMesh.count = normalCount;
    this.superRingMesh.count = superCount;
    this.normalMesh.instanceMatrix.needsUpdate = true;
    this.superMesh.instanceMatrix.needsUpdate = true;
    this.ringMesh.instanceMatrix.needsUpdate = true;
    this.superRingMesh.instanceMatrix.needsUpdate = true;
    return { collected, superCollected };
  }

  activeCount(): number {
    let count = 0;
    for (const cell of this.cells) if (cell.active) count += 1;
    return count;
  }

  reset(): void {
    for (const cell of this.cells) {
      cell.active = false;
      cell.popTime = 0;
    }
    for (const mesh of [this.normalMesh, this.superMesh, this.ringMesh, this.superRingMesh]) {
      mesh.count = 0;
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  dispose(): void {
    for (const item of this.disposables) item.dispose();
    for (const mesh of [this.normalMesh, this.superMesh, this.ringMesh, this.superRingMesh]) {
      mesh.dispose();
    }
  }
}
